/**
 * WP3 (Story #3327, Bug #3308) — single source of truth for the novelty
 * score colour palette.
 *
 * Pre-WP3 the overall NoveltyScore widget (ProjectPriorArtPanel) and the
 * per-claim novelty rows (DocumentDetail) used different threshold values
 * (overall: green ≥ 80 / amber 60-79 / red < 60; per-claim: green > 70 /
 * amber 40-70 / red < 40), so a project could display green overall while
 * every per-claim row showed red. This helper centralises the canonical
 * thresholds and is consumed by both sites.
 */
export interface NoveltyColourSet {
  text: string
  bg: string
  bar: string
}

export function noveltyColours(score: number): NoveltyColourSet {
  if (score >= 80) {
    return { text: 'text-green-600', bg: 'bg-green-50', bar: 'bg-green-500' }
  }
  if (score >= 60) {
    return { text: 'text-amber-600', bg: 'bg-amber-50', bar: 'bg-amber-500' }
  }
  return { text: 'text-red-600', bg: 'bg-red-50', bar: 'bg-red-500' }
}

/**
 * BUG-RETEST-B: derive a human-readable verdict from the per-claim novelty
 * score. This is the AUTHORITATIVE label shown to the user; it is derived
 * from the numeric score rather than from the LLM assessment text (which can
 * be inconsistent with the score). The LLM assessment is kept as supplementary
 * context only.
 */
export function claimNoveltyLabel(score: number): string {
  if (score >= 80) return 'Highly novel'
  if (score >= 60) return 'Moderately novel'
  if (score >= 40) return 'Limited novelty'
  return 'Low novelty — significant prior art'
}

// Hex equivalents so callers needing inline style colours (e.g. for
// border colours) do not have to map class names back to hex themselves.
export const NOVELTY_BAR_HEX: Record<string, string> = {
  'bg-green-500': '#22c55e',
  'bg-amber-500': '#f59e0b',
  'bg-red-500': '#ef4444',
}
